/**
 * Cloudflare Worker 同期クライアント
 *
 * - 設定でトークン + デバイスIDを保存
 * - 自動同期: Wi-Fi 接続検知時 or 起動時
 * - 画像は別エンドポイント PUT
 *
 * Phase 12 実装。Phase 13 で本番デプロイ後に動作テスト。
 */

import { listShoots, saveShoot, getShoot, deleteShoot } from './store.js';

const TOKEN_KEY = 'zn.sync.token';
const DEVICE_KEY = 'zn.sync.device';
const ENDPOINT_KEY = 'zn.sync.endpoint';
const LAST_SYNC_KEY = 'zn.sync.lastSync';

export function getSyncConfig() {
  return {
    token: localStorage.getItem(TOKEN_KEY) || '',
    deviceId: localStorage.getItem(DEVICE_KEY) || '',
    endpoint: localStorage.getItem(ENDPOINT_KEY) || '',
  };
}

export function setSyncConfig({ token, deviceId, endpoint }) {
  if (token != null) localStorage.setItem(TOKEN_KEY, token);
  if (deviceId != null) localStorage.setItem(DEVICE_KEY, deviceId);
  if (endpoint != null) localStorage.setItem(ENDPOINT_KEY, endpoint);
}

export function ensureDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function isSyncConfigured() {
  const c = getSyncConfig();
  return Boolean(c.token && c.endpoint);
}

function buildHeaders() {
  const { token, deviceId } = getSyncConfig();
  return {
    'Authorization': `Bearer ${token}`,
    'X-Device-Id': deviceId,
  };
}

async function api(path, init = {}) {
  const { endpoint } = getSyncConfig();
  if (!endpoint) throw new Error('endpoint not set');
  const res = await fetch(endpoint.replace(/\/$/, '') + path, {
    ...init,
    headers: { ...buildHeaders(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${path} ${res.status}: ${text.slice(0, 200)}`);
  }
  const ct = res.headers.get('content-type') || '';
  return ct.includes('json') ? await res.json() : await res.blob();
}

// --------------------------------------------------
// シーン同期
// --------------------------------------------------

export async function pushShoot(shoot) {
  if (!isSyncConfigured()) return;

  // 画像をアップロード（変更があったもののみ）
  if (shoot.croppedImageBlob) {
    await api(`/api/images/${shoot.id}/cropped`, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/jpeg' },
      body: shoot.croppedImageBlob,
    });
  }
  if (shoot.monoImageBlob) {
    await api(`/api/images/${shoot.id}/mono`, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/jpeg' },
      body: shoot.monoImageBlob,
    });
  }

  // メタデータ送信（Blob は送らない、参照のみ）
  const meta = { ...shoot };
  delete meta.croppedImageBlob;
  delete meta.monoImageBlob;
  delete meta.originalImageBlob;
  await api(`/api/shoots/${shoot.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(meta),
  });
}

/**
 * 前回の同期以降にサーバーで更新されたシーンを取り込む。
 * 最終同期時刻はここでは更新しない（performFullSync が送信の成否を見て更新する）。
 * @returns {{ pulled: number, pulledIds: Set<string> }}
 */
export async function pullChanges(since = lastSyncTime()) {
  if (!isSyncConfigured()) return { pulled: 0, pulledIds: new Set() };
  const result = await api(`/api/shoots?since=${since}`);
  const pulledIds = new Set();
  for (const summary of (result.shoots || [])) {
    const local = await getShoot(summary.id);
    if (local && local.updatedAt >= summary.updated_at) continue;

    const data = await api(`/api/shoots/${summary.id}`);
    try { data.croppedImageBlob = await api(`/api/images/${summary.id}/cropped`); } catch {}
    try { data.monoImageBlob = await api(`/api/images/${summary.id}/mono`); } catch {}
    await saveShoot(data);
    pulledIds.add(summary.id);
  }
  return { pulled: pulledIds.size, pulledIds };
}

/**
 * 前回の同期以降に端末で更新したシーンを送る。
 * いま取り込んだばかりのシーン（skipIds）は送り返さない。
 * @returns {{ pushed: number, failed: number }}
 */
export async function pushAllChanges(since = lastSyncTime(), skipIds = new Set()) {
  if (!isSyncConfigured()) return { pushed: 0, failed: 0 };
  const local = await listShoots({ limit: 1000 });
  const target = local.filter((s) => (s.updatedAt || 0) > since && !skipIds.has(s.id));
  let pushed = 0;
  let failed = 0;
  for (const s of target) {
    try {
      await pushShoot(s);
      pushed++;
    } catch (e) {
      failed++;
      console.warn('[sync] push failed:', s.id, e);
    }
  }
  return { pushed, failed };
}

function lastSyncTime() {
  return parseInt(localStorage.getItem(LAST_SYNC_KEY) || '0', 10) || 0;
}

/**
 * 受信 → 送信。どちらも「前回の同期時刻」を基準にし、同期を始めた時刻を次の基準にする。
 * （以前は受信の直後に基準時刻を今にしていたため、同期前に保存したシーンが送られなかった）
 * 送信に 1 件でも失敗したら基準時刻を進めず、次回もう一度送る。
 */
export async function performFullSync() {
  if (!isSyncConfigured()) return { skipped: true };
  const since = lastSyncTime();
  const startedAt = Date.now();
  const pull = await pullChanges(since);
  const push = await pushAllChanges(since, pull.pulledIds);
  if (push.failed === 0) localStorage.setItem(LAST_SYNC_KEY, String(startedAt));
  return { pulled: pull.pulled, pushed: push.pushed, failed: push.failed };
}

export async function healthCheck() {
  if (!isSyncConfigured()) return false;
  try {
    const { endpoint } = getSyncConfig();
    const res = await fetch(endpoint.replace(/\/$/, '') + '/api/health');
    return res.ok;
  } catch {
    return false;
  }
}
